import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getCoachAthletes,
  coachMarkPayment,
  getCoachCashSummary,
  type CoachAthletesResponse,
  type CoachCashSummary,
  type PaymentMethod,
  type AthleteProduct,
} from "../../../services/api";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameIcon, GameCard, GameButton } from "../../../components/game";
import { matchesQuery } from "../../../lib/search";
import CoachPaymentStatus from "../../../components/app/CoachPaymentStatus";

export default function AppCoachPayments() {
  const { t } = useTranslation();

  const [data, setData] = useState<CoachAthletesResponse | null>(null);
  const [cashSummary, setCashSummary] = useState<CoachCashSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [teamFilter, setTeamFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Payment modal state
  const [paymentModal, setPaymentModal] = useState<{
    open: boolean;
    athleteProductId: string;
    athleteName: string;
    productName: string;
    amount: number;
    currency: string;
  } | null>(null);
  const [paymentForm, setPaymentForm] = useState<{
    method: PaymentMethod;
    bank_reference: string;
    notes: string;
  }>({ method: "cash", bank_reference: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [teamFilter]);

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const [athletesData, summaryData] = await Promise.all([
        getCoachAthletes(teamFilter || undefined),
        getCoachCashSummary(),
      ]);
      setData(athletesData);
      setCashSummary(summaryData);
    } catch (err) {
      console.error("Failed to load data:", err);
      setError(true);
    }
    setLoading(false);
  };

  // Diacritic- and order-insensitive, and across the team name too: the
  // roster holds "Mureșan Ștefan", the coach types "stefan muresan", and the
  // plain substring match this used to do returned nothing at all.
  const filteredAthletes =
    data?.athletes.filter(athlete =>
      matchesQuery(
        searchQuery,
        athlete.full_name,
        athlete.email,
        athlete.athlete_profiles?.team?.name,
        ...(athlete.teams || []).map(team => team.name)
      )
    ) || [];

  // Only show athletes with pending/overdue payments
  const athletesWithPayments = filteredAthletes.filter(a =>
    a.payment_summary?.has_pending || a.payment_summary?.has_overdue
  );

  const handleOpenPaymentModal = (
    athleteProductId: string,
    athleteName: string,
    productName: string,
    amount: number,
    currency: string
  ) => {
    setPaymentModal({ open: true, athleteProductId, athleteName, productName, amount, currency });
    setPaymentForm({ method: "cash", bank_reference: "", notes: "" });
  };

  const handleMarkPayment = async () => {
    if (!paymentModal) return;
    setSaving(true);
    try {
      await coachMarkPayment(paymentModal.athleteProductId, {
        payment_method: paymentForm.method,
        bank_reference: paymentForm.method === "bank_transfer" ? paymentForm.bank_reference : undefined,
        notes: paymentForm.notes || undefined,
      });
      setPaymentModal(null);
      await loadData();
    } catch (err) {
      console.error("Failed to mark payment:", err);
    }
    setSaving(false);
  };

  const formatCurrency = (cents: number, currency = "RON") => {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  };

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-800">{t("coach.payments", "Plăți")}</h1>
        </div>

        {/* Cash Summary Card */}
        {cashSummary && cashSummary.pending_cash_cents > 0 && (
          <GameCard className="p-4 ring-1 ring-amber-200">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-amber-100 flex items-center justify-center shadow-[0_3px_0_#fde68a]">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800">
                  {t("coach.cashToHandOver", "Numerar de predat")}
                </p>
                <p className="text-xl font-extrabold text-amber-700 tabular-nums">
                  {formatCurrency(cashSummary.pending_cash_cents)}
                </p>
                <p className="text-xs text-amber-600">
                  {cashSummary.pending_payments.length} {t("coach.payments", "plăți")}
                </p>
              </div>
            </div>
          </GameCard>
        )}

        {/* Where a coach starts: which teams are behind, before any list of
            individual fees. Reads the periods, so it agrees with what the
            parent sees in their own app. */}
        <CoachPaymentStatus />

        {/* Filters */}
        <div className="space-y-3">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t("coach.searchAthletes", "Cauta sportivi...")}
              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-slate-800 shadow-[0_3px_0_#eef2f8] focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {data && data.teams.length > 1 && (
            <select
              value={teamFilter}
              onChange={e => setTeamFilter(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl text-slate-800 shadow-[0_3px_0_#eef2f8] focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">{t("coach.allTeams", "Toate echipele")}</option>
              {data.teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Athletes List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <GameCard className="p-6 text-center">
            <h3 className="text-base font-bold text-slate-800 mb-1">{t("common.loadErrorTitle", "Ceva n-a mers")}</h3>
            <p className="text-sm text-slate-500">
              {t("common.loadError", "Nu am putut încărca datele. Verifică conexiunea și încearcă din nou.")}
            </p>
            <button
              onClick={loadData}
              className="mt-3 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white"
            >
              {t("common.retry", "Reîncearcă")}
            </button>
          </GameCard>
        ) : athletesWithPayments.length === 0 ? (
          <GameCard className="p-8 text-center">
            <div className="w-16 h-16 rounded-3xl bg-emerald-100 grid place-items-center mx-auto mb-4 text-emerald-600 shadow-[0_3px_0_#d1fae5]">
              <GameIcon name="check" className="w-8 h-8" />
            </div>
            <p className="text-slate-500">{t("coach.noPaymentsDue", "Toate plățile sunt la zi!")}</p>
          </GameCard>
        ) : (
          <div className="space-y-3">
            {athletesWithPayments.map(athlete => {
              const isOverdue = athlete.payment_summary?.has_overdue;
              return (
              <GameCard key={athlete.id} className={`p-4 ${isOverdue ? "ring-1 ring-red-200" : "ring-1 ring-amber-200"}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white font-semibold">
                    {athlete.full_name?.charAt(0) || "?"}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-800">{athlete.full_name}</h3>
                    <p className="text-xs text-slate-500">
                      {athlete.athlete_profiles?.team?.name || t("coach.noTeam", "Fără echipa")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-extrabold tabular-nums ${isOverdue ? "text-red-600" : "text-amber-600"}`}>
                      {formatCurrency(athlete.payment_summary?.total_due_cents || 0)}
                    </p>
                    <p className="text-xs text-slate-500">{t("coach.due", "de plata")}</p>
                  </div>
                </div>

                {/* Products with pending payments */}
                <div className="space-y-2 border-t border-slate-100 pt-3">
                  {athlete.athlete_products?.filter((p: AthleteProduct) => p.payment_status !== "paid").map((product: AthleteProduct) => {
                    const productOverdue = product.payment_status === "overdue";
                    return (
                    <div key={product.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-xl">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-700 truncate">{product.product?.name}</p>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${
                            productOverdue ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                          }`}>
                            <GameIcon name={productOverdue ? "flame" : "clock"} className="w-3 h-3" />
                            {productOverdue ? t("payment.overdue", "Întârziat") : t("payment.pending", "De plata")}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500">
                          {formatCurrency(product.product?.price_cents || 0, product.product?.currency)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleOpenPaymentModal(
                          product.id,
                          athlete.full_name || "",
                          product.product?.name || "",
                          product.product?.price_cents || 0,
                          product.product?.currency || "RON"
                        )}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-white text-sm font-bold rounded-xl bg-gradient-to-b from-emerald-400 to-emerald-500 shadow-[0_3px_0_#059669] active:translate-y-[2px] transition-transform"
                      >
                        <GameIcon name="check" className="w-4 h-4 text-white" />
                        {t("coach.markPaid", "Plătit")}
                      </button>
                    </div>
                    );
                  })}
                </div>
              </GameCard>
              );
            })}
          </div>
        )}

        {/* Payment Modal */}
        {paymentModal && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)] sm:pt-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] sm:pb-4 !mt-0">
            {/* Capped and split into three: the header and the buttons stay
                put, the form between them scrolls. Without the cap a tall form
                pushed its own buttons past the bottom of the screen, and the
                footer nav sat on top of whatever was left. */}
            <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl flex flex-col max-h-full">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
                <h2 className="text-lg font-bold text-slate-800">{t("coach.confirmPayment", "Confirma plata")}</h2>
                <button
                  onClick={() => setPaymentModal(null)}
                  className="p-2 hover:bg-slate-100 rounded-full"
                >
                  <GameIcon name="x" className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              <div className="flex-1 min-h-0 p-5 space-y-4 overflow-y-auto">
                {/* Payment Details */}
                <div className="bg-slate-50 rounded-2xl p-4">
                  <p className="text-sm text-slate-600">{paymentModal.athleteName}</p>
                  <p className="font-semibold text-slate-800">{paymentModal.productName}</p>
                  <p className="text-xl font-extrabold text-slate-900 tabular-nums mt-1">
                    {formatCurrency(paymentModal.amount, paymentModal.currency)}
                  </p>
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("coach.paymentMethod", "Metoda de plata")}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: "cash" as PaymentMethod, label: t("coach.cash", "Numerar"), icon: "M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" },
                      { value: "card" as PaymentMethod, label: t("coach.card", "Card"), icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" },
                      { value: "bank_transfer" as PaymentMethod, label: t("coach.transfer", "Transfer"), icon: "M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" },
                    ].map(method => (
                      <button
                        key={method.value}
                        type="button"
                        onClick={() => setPaymentForm({ ...paymentForm, method: method.value })}
                        className={`p-3 rounded-2xl border-2 transition-all ${
                          paymentForm.method === method.value
                            ? "border-indigo-500 bg-indigo-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <svg className={`w-6 h-6 mx-auto ${paymentForm.method === method.value ? "text-indigo-600" : "text-slate-400"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={method.icon} />
                        </svg>
                        <p className={`text-xs mt-1 font-medium ${paymentForm.method === method.value ? "text-indigo-600" : "text-slate-500"}`}>
                          {method.label}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Bank Reference (for transfers) */}
                {paymentForm.method === "bank_transfer" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("coach.bankReference", "Referință bancară")}
                    </label>
                    <input
                      type="text"
                      value={paymentForm.bank_reference}
                      onChange={e => setPaymentForm({ ...paymentForm, bank_reference: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="ex: OP123456"
                    />
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("coach.notes", "Note")} ({t("common.optional", "opțional")})
                  </label>
                  <textarea
                    value={paymentForm.notes}
                    onChange={e => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    rows={2}
                    placeholder={t("coach.notesPlaceholder", "Adauga o nota...")}
                  />
                </div>

                {/* Cash Warning */}
                {paymentForm.method === "cash" && (
                  <div className="bg-amber-50 rounded-2xl p-4 flex items-start gap-3">
                    <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="text-sm text-amber-700">
                      <p className="font-medium">{t("coach.cashWarningTitle", "Plata in numerar")}</p>
                      <p>{t("coach.cashWarningText", "Aceasta suma va fi inregistrata ca incasata de tine si trebuie predata administratorului clubului.")}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="px-5 py-4 border-t border-slate-100 flex gap-3 shrink-0">
                <GameButton
                  variant="ghost"
                  className="flex-1"
                  onClick={() => setPaymentModal(null)}
                >
                  {t("common.cancel", "Anulează")}
                </GameButton>
                <GameButton
                  variant="mint"
                  className="flex-1"
                  onClick={handleMarkPayment}
                  disabled={saving}
                >
                  {saving ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    t("coach.confirmPayment", "Confirma plata")
                  )}
                </GameButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </MobileAppLayout>
  );
}
